import React, { useCallback, useRef } from 'react';
import * as d3 from 'd3';
import classNames from 'classnames';
import { Col } from 'components/@codelitt/ay-design-library';

import { colors } from 'constants/colors';
import GraphContainer from '../GraphContainer';
import styles from './TimeLineGraph.module.scss';
import TimelineBubble from './Elements/TimelineBubble';
import CenterLine from './Elements/CenterLine';
import {
  TimeLineGraphProps,
  TimelineBubbleType,
  TimelinePositions,
} from './types';
import {
  DEFAULT_GRAPH_DIMENSIONS,
  getLinePositionId,
  getOuterElementPositionId,
  TIMELINE_GREEN_TOOLTIP_LINE_ID,
  TimeLineBubbleIds,
  TIMELINE_GRAPH_ID,
  TIMELINE_CENTER_LINE_ID,
  TIMELINE_HORIZONTAL_LINE_ID,
} from './constants';

const TimeLineGraph: React.FC<TimeLineGraphProps> = props => {
  const {
    data,
    width = DEFAULT_GRAPH_DIMENSIONS.width,
    height = DEFAULT_GRAPH_DIMENSIONS.height,
    className,
    onBubbleClick,
    onBubbleHover,
  } = props;

  const graphContainerElement = useRef(null);
  const isReady = !!data.bubbles?.length;

  const toggleD3Element = useCallback((id: string, show: boolean) => {
    d3.select(id).attr('display', show ? '' : 'none');
  }, []);

  const applyHoverPurple = useCallback(
    (position: string) => {
      // Show horizontal line for purple bubbles
      const purpleBubbleFirst = data.bubbles.find(
        b => b.id === TimeLineBubbleIds.PURPLE_1,
      );
      const purpleBubbleSecond = data.bubbles.find(
        b => b.id === TimeLineBubbleIds.PURPLE_2,
      );

      if (purpleBubbleFirst || purpleBubbleSecond) {
        if (purpleBubbleSecond) {
          d3.select(`#bubble-${purpleBubbleSecond.position}`).attr(
            'fill-opacity',
            1,
          );
        }
        // Show horizontal lines from purple bubbles
        const purpleBubbles = data.bubbles.filter(
          b =>
            b.id === TimeLineBubbleIds.PURPLE_1 ||
            b.id === TimeLineBubbleIds.PURPLE_2,
        );
        purpleBubbles.forEach((_, index) => {
          toggleD3Element(`#${TIMELINE_HORIZONTAL_LINE_ID}-${index}`, true);
        });
      }

      // Show outer elements and lines for the hovered position
      toggleD3Element(`#${getOuterElementPositionId(position)}`, true);
      toggleD3Element(`#${getLinePositionId(position)}`, true);

      // Show outer elements and lines for specific bubble positions
      const positions = [
        data.bubbles.find(b => b.id === TimeLineBubbleIds.PURPLE_1)?.position,
        data.bubbles.find(b => b.id === TimeLineBubbleIds.PURPLE_2)?.position,
      ];

      positions.forEach(p => {
        if (p && p !== TimelinePositions.RIGHT) {
          toggleD3Element(`#${getOuterElementPositionId(p)}`, true);
          toggleD3Element(`#${getLinePositionId(p)}`, true);
        }
      });

      // Always show close-right position line only
      toggleD3Element(
        `#${getLinePositionId(TimelinePositions.CLOSE_RIGHT)}`,
        true,
      );

      // Update bubble styles for purple bubbles
      const updateBubble = (pos: string) => {
        d3.select(`#bubble-${pos}`)
          .attr('fill-opacity', 1)
          .style('fill', colors.primaryColor300)
          .style('stroke', colors.primaryColor300);
      };

      if (positions.includes(TimelinePositions.RIGHT))
        updateBubble(TimelinePositions.RIGHT);
      if (positions.includes(TimelinePositions.LEFT))
        updateBubble(TimelinePositions.LEFT);
    },
    [data.bubbles, toggleD3Element],
  );

  const applyHoverGreen = useCallback(() => {
    const greenBubble = data.bubbles.find(
      b => b.id === TimeLineBubbleIds.GREEN,
    );
    if (!greenBubble) return;

    // Show green tooltip line
    toggleD3Element(`#${TIMELINE_GREEN_TOOLTIP_LINE_ID}`, true);
    toggleD3Element(
      `#${getOuterElementPositionId(greenBubble.position)}`,
      true,
    );
    toggleD3Element(`#${getLinePositionId(greenBubble.position)}`, true);
  }, [data.bubbles, toggleD3Element]);

  const onMouseOutBubble = useCallback(() => {
    // Hide horizontal lines from purple bubbles
    const purpleBubbles = data.bubbles.filter(
      b =>
        b.id === TimeLineBubbleIds.PURPLE_1 ||
        b.id === TimeLineBubbleIds.PURPLE_2,
    );
    purpleBubbles.forEach((_, index) => {
      toggleD3Element(`#${TIMELINE_HORIZONTAL_LINE_ID}-${index}`, false);
    });

    // Hide green tooltip line
    toggleD3Element(`#${TIMELINE_GREEN_TOOLTIP_LINE_ID}`, false);

    // Reset bubble styles to original
    const purpleBubbleFirst = data.bubbles.find(
      b => b.id === TimeLineBubbleIds.PURPLE_1,
    );
    if (purpleBubbleFirst) {
      d3.select(`#bubble-${purpleBubbleFirst.position}`)
        .style('fill', purpleBubbleFirst.fillColor)
        .style('stroke', purpleBubbleFirst.strokeColor);
    }

    const purpleBubbleSecond = data.bubbles.find(
      b => b.id === TimeLineBubbleIds.PURPLE_2,
    );
    if (purpleBubbleSecond) {
      d3.select(`#bubble-${purpleBubbleSecond.position}`)
        .style(
          'fill',
          purpleBubbleSecond.hasOpacity
            ? purpleBubbleSecond.secondaryColor || purpleBubbleSecond.fillColor
            : purpleBubbleSecond.fillColor,
        )
        .style('stroke', purpleBubbleSecond.strokeColor);
    }

    // Hide all outer elements and lines
    Object.values(TimelinePositions).forEach(position => {
      toggleD3Element(`#${getOuterElementPositionId(position)}`, false);
      toggleD3Element(`#${getLinePositionId(position)}`, false);
    });

    // Hide center line
    toggleD3Element(`#${TIMELINE_CENTER_LINE_ID}`, false);

    // Hide horizontal line from right bubble to center
    toggleD3Element('#right-to-center-line', false);
  }, [data.bubbles, toggleD3Element]);

  const onMouseOverBubble = useCallback(
    (bubble: TimelineBubbleType) => {
      const isGreenBubble = bubble.id === TimeLineBubbleIds.GREEN;

      if (isGreenBubble) {
        applyHoverGreen();
      } else {
        applyHoverPurple(bubble.position);
      }

      // Call hover callback if provided
      onBubbleHover?.(bubble);
    },
    [applyHoverGreen, applyHoverPurple, onBubbleHover],
  );

  const drawBubbles = useCallback(() => {
    if (!isReady || !data.bubbles || data.bubbles.length === 0) {
      return null;
    }

    return data.bubbles.map(bubble => (
      <TimelineBubble
        key={bubble.id}
        bubble={bubble}
        onMouseOverBubble={onMouseOverBubble}
        onMouseOutBubble={onMouseOutBubble}
        onBubbleClick={onBubbleClick}
      />
    ));
  }, [
    isReady,
    data.bubbles,
    onMouseOverBubble,
    onMouseOutBubble,
    onBubbleClick,
  ]);

  if (!data.bubbles || data.bubbles.length === 0) {
    return null;
  }

  return (
    <Col lg={4} md={4} sm={4} xs={4}>
      <GraphContainer
        wrapperClassName={`${styles.container} ${className || ''}`}
      >
        <div className={styles['graph-content']}>
          <div
            className={styles['graph-titles']}
            onMouseOver={() => onMouseOverBubble(data.bubbles[0])}
            onMouseOut={onMouseOutBubble}
          >
            <span className={styles.title}>{data.labels.header.title}</span>
            <span className={styles.value}>{data.labels.header.value}</span>
            <span className={styles.subtitle}>
              {data.labels.header.subtitle}
            </span>
          </div>

          {isReady && (
            <div ref={graphContainerElement}>
              <svg id={TIMELINE_GRAPH_ID} width={width} height={height}>
                <CenterLine bubbles={data.bubbles} />
                {drawBubbles()}
              </svg>
            </div>
          )}

          <div
            className={classNames([
              styles['graph-titles'],
              styles['graph-titles-footer'],
            ])}
            onMouseOver={() =>
              onMouseOverBubble(data.bubbles[data.bubbles.length - 1])
            }
            onMouseOut={onMouseOutBubble}
          >
            <span className={styles.title}>{data.labels.footer.title}</span>
            <span className={styles.value}>{data.labels.footer.value}</span>
            <span className={styles.subtitle}>
              {data.labels.footer.subtitle}
            </span>
          </div>
        </div>
      </GraphContainer>
    </Col>
  );
};

export default TimeLineGraph;
